import {CellIndex, MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {computeIsRealIndex} from 'notebook/tables/store/logic/TableSelectionLogic';
import {Table} from 'notebook/tables/TableConstants';
import {isRowArchived} from 'notebook/tables/util/isRowArchived';

/**
 * Logic for table locking and permissions about the editability of the table.
 */

export interface EvaluatedCellWithPosition {
  cellId: CellIndex;
  evaluatedCell: EvaluatedCell;
}

/**
 * Computes whether the table is in a locked state.
 * A table is locked if the locked prop is true OR if it has placeholder rows.
 *
 * @param locked - Whether the table is locked for editing
 * @param hasPlaceholderRows - Whether the table has placeholder rows
 */
export function computeIsTableLocked(locked: boolean, hasPlaceholderRows: boolean): boolean {
  return locked || hasPlaceholderRows;
}

/**
 * Returns true if any of the given cells are locked or in an archived row.
 */
export function computeAnyCellsLockedOrArchived(
  cells: Array<EvaluatedCellWithPosition>,
  rowData: Table['rowData']
): boolean {
  return cells.some(
    ({evaluatedCell, cellId: cellPosition}) =>
      evaluatedCell.locked || isRowArchived(rowData?.[cellPosition.row])
  );
}

/**
 * Returns evaluated cells within a selection box that correspond to real table indices.
 */
export function computeEvaluatedCellsInBox(
  box: MultiSelectionBox,
  computedTable: Array<Array<EvaluatedCell>>,
  table: Table
): Array<EvaluatedCellWithPosition> {
  const evaluatedCells: Array<EvaluatedCellWithPosition> = [];
  const {minI, maxI, minJ, maxJ} = box;
  for (let i = minI; i <= maxI; i++) {
    for (let j = minJ; j <= maxJ; j++) {
      if (computeIsRealIndex([i, j], table)) {
        evaluatedCells.push({
          evaluatedCell: computedTable[i][j],
          cellId: {row: i, column: j},
        });
      }
    }
  }
  return evaluatedCells;
}

export interface ComputeIsCellSelectionEditingLockedParams {
  isTableLocked: boolean;
  selectedCells: Array<EvaluatedCellWithPosition>;
  rowData: Table['rowData'];
  forUndoRedo?: boolean;
}

/**
 * Whether editing is locked for the current cell selection.
 */
export function computeIsCellSelectionEditingLocked({
  isTableLocked,
  selectedCells,
  rowData,
  forUndoRedo,
}: ComputeIsCellSelectionEditingLockedParams): boolean {
  const areAnySelectedCellsLocked = computeAnyCellsLockedOrArchived(selectedCells, rowData);
  return (
    isTableLocked ||
    // Allow user to undo/redo an action involving locked cells since we know both states are valid
    (forUndoRedo ? false : areAnySelectedCellsLocked)
  );
}

export interface ComputeLockedWarningMessageParams {
  editingIsLocked: boolean;
  isTableLocked: boolean;
  editWhileTableLockedWarning: string | null;
  getEditWhileCellLockedWarning: ((selection: MultiSelectionBox) => string) | null;
  selection: MultiSelectionBox | null;
}

/**
 * Returns the warning message to show when editing is locked, or null if no warning applies.
 */
export function computeLockedWarningMessage({
  editingIsLocked,
  isTableLocked,
  editWhileTableLockedWarning,
  getEditWhileCellLockedWarning,
  selection,
}: ComputeLockedWarningMessageParams): string | null {
  if (!editingIsLocked) {
    return null;
  }
  if (isTableLocked && editWhileTableLockedWarning) {
    return editWhileTableLockedWarning;
  }
  if (!isTableLocked && getEditWhileCellLockedWarning && selection) {
    return getEditWhileCellLockedWarning(selection);
  }
  return null;
}

export interface ComputeSelectedEvaluatedCellsParams {
  hasMultiSelection: boolean;
  expandedBox: MultiSelectionBox | null;
  computedTable: Array<Array<EvaluatedCell>>;
  table: Table;
}

export function computeSelectedEvaluatedCells({
  hasMultiSelection,
  expandedBox,
  computedTable,
  table,
}: ComputeSelectedEvaluatedCellsParams): Array<EvaluatedCellWithPosition> {
  if (!hasMultiSelection || expandedBox == null) {
    return [];
  }
  return computeEvaluatedCellsInBox(expandedBox, computedTable, table);
}

export interface ComputeAnchorSelectedEvaluatedCellsParams {
  hasMultiSelection: boolean;
  multiStartIndex: TableIndex | null;
  computedTable: Array<Array<EvaluatedCell>>;
  table: Table;
}

export function computeAnchorSelectedEvaluatedCells({
  hasMultiSelection,
  multiStartIndex,
  computedTable,
  table,
}: ComputeAnchorSelectedEvaluatedCellsParams): Array<EvaluatedCellWithPosition> {
  if (!hasMultiSelection || multiStartIndex == null) {
    return [];
  }
  const [rowIndex, columnIndex] = multiStartIndex;
  if (!computeIsRealIndex([rowIndex, columnIndex], table)) {
    return [];
  }
  const anchorCell: EvaluatedCellWithPosition = {
    evaluatedCell: computedTable[rowIndex][columnIndex],
    cellId: {row: rowIndex, column: columnIndex},
  };
  if (anchorCell.evaluatedCell.mergedBox) {
    return computeEvaluatedCellsInBox(anchorCell.evaluatedCell.mergedBox, computedTable, table);
  }
  return [anchorCell];
}
